import AOS from "aos";
import "aos/dist/aos.css";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ClientAuthContext from "../context/ClientAuthContext";
import "../css/home.scss";
import { serverInstance } from "../service/axios";
import Benefits from "./benefits/Benefits";
import CenturionSection from "./CenturionSection";
import CustomPopup from "./popup/CustomPopup";
import Dimmer from "./shared/Dimmer";
import Modal from "./shared/Modal";

const CUSTOM_POPUP_ID = "CUSTOM_POPUP";
const HOME_IMAGE_CACHE_KEY = "msidHomeImage";
const ASSET_BASE_URL = "https://msid-asset.s3.ap-northeast-2.amazonaws.com";
const DEFAULT_HOME_IMAGE_URL = `${ASSET_BASE_URL}/settings/HOME_IMAGE.jpg`;
const DEFAULT_GUEST_TITLE = "МОНГОЛЫН АРЬС СУДЛАЛЫН САЛБАРЫГ ХӨГЖҮҮЛЭХ";
const DEFAULT_GUEST_TEXT = "Арьс судлалаар эрдэм шинжилгээ, судалгааны ажил хийж буй эмч, судлаачдыг дэмжих, тэдний мэдлэг мэргэжлээ тасралтгүйгээр дээшлүүлэх, арьс судлалын салбарыг хөгжүүлэх зорилготой";
const DEFAULT_GUEST_BUTTON_TEXT = "Гишүүн болох";
const DEFAULT_GUEST_BUTTON_LINK = "/signup";

const buildHomeImageUrl = (fileUrl) => {
  if (!fileUrl) {
    return DEFAULT_HOME_IMAGE_URL;
  }
  if (/^https?:\/\//i.test(fileUrl)) {
    return fileUrl;
  }
  return `${ASSET_BASE_URL}/${fileUrl}`;
};

const preloadHomeImage = (url) => {
  if (typeof window === "undefined" || !url) {
    return;
  }
  const img = new Image();
  img.src = url;
};

const readJsonCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeJsonCache = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data));
};

const readHomeImageCache = () => readJsonCache(HOME_IMAGE_CACHE_KEY);

const writeHomeImageCache = (data) => {
  writeJsonCache(HOME_IMAGE_CACHE_KEY, data);
};

const isHomeImageDataEqual = (cached, serverData) => {
  if (!cached || !serverData) {
    return false;
  }
  return cached.link === serverData.link && cached.fileUrl === serverData.fileUrl;
};

const getLocalizedSettingText = (item, language, fallback) => {
  if (item?.value_en && language) {
    return item.value_en;
  }
  if (item?.value) {
    return item.value;
  }
  return fallback;
};

const cachedHomeImageOnLoad = readHomeImageCache();
const initialHomeImageUrl = buildHomeImageUrl(cachedHomeImageOnLoad?.fileUrl);
preloadHomeImage(initialHomeImageUrl);

const Home = ({ data, benefitData, language }) => {

  const navigate = useNavigate();
  const { userData, setUserData } = useContext(ClientAuthContext)
  const isLoggedIn = !!userData?.id;
  const [loading, setLoading] = useState(false);
  const [leftLinks, setLeftLinks] = useState([]);
  const [rightLinks, setRightLinks] = useState([]);
  const [homeLink, setHomeLink] = useState(() => cachedHomeImageOnLoad?.link || "");
  const [homeImageUrl, setHomeImageUrl] = useState(() => initialHomeImageUrl);
  const [homeData, setHomeData] = useState(null);
  const [popupVisible, setPopupVisible] = useState(null);
  const [guestContentLoaded, setGuestContentLoaded] = useState(false);
  const [homeTitle, setHomeTitle] = useState("");
  const [homeText, setHomeText] = useState("");
  const [homeButtonLink, setHomeButtonLink] = useState("");
  const [homeButtonText, setHomeButtonText] = useState("");
  const [animatedCounts, setAnimatedCounts] = useState({
    clinic: 0,
    doctor: 0,
    journal: 0,
    training: 0,
    meeting: 0
  });
  const [width, setWidth] = useState(window.innerWidth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCustomPopupOpen, setIsCustomPopupOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [isMemberModalOpen, setMemberModalOpen] = useState(false);

  useEffect(() => {
    AOS.init();
    loadHomeItems();
    getUserData();
    getItemsData();
    
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    loadGuestItems();
  }, [language]);

  useEffect(() => {
    const leftSide = {
      menu: isLoggedIn ? data.leftMenu.slice(4, 8) : data.leftMenu.slice(0, 4),
      links: isLoggedIn ? data.leftLinks.slice(4, 8) : data.leftLinks.slice(0, 4)
    };
        
    const rightSide = {
      menu: isLoggedIn ? data.rightMenu.slice(4, 8) : data.rightMenu.slice(0, 4),
      links: isLoggedIn ? data.rightLinks.slice(4, 8) : data.rightLinks.slice(0, 4)
    };
  
    // Create the combined link objects
    const rightObjs = leftSide.menu.map((menu, index) => ({
      text: menu,
      link: leftSide.links[index]
    }));
  
    const leftObjs = rightSide.menu.map((menu, index) => ({
      text: menu,
      link: rightSide.links[index]
    }));
  
    setLeftLinks(rightObjs);
    setRightLinks(leftObjs);
  }, [data, isLoggedIn]);

  useEffect(() => {
    if (homeData) {
      // Start with all values at 0
      setAnimatedCounts({
        clinic: 0,
        doctor: 0,
        journal: 0,
        training: 0,
        meeting: 0
      });
      
      // Create animation intervals for each counter
      const intervalId = setInterval(() => {
        setAnimatedCounts(prev => {
          const newCounts = { ...prev };
          let allComplete = true;
          
          // Update each counter by 1 if it hasn't reached its target
          Object.keys(newCounts).forEach(key => {
            if (newCounts[key] < homeData[key]) {
              newCounts[key] = Math.min(newCounts[key] + 3, homeData[key]);
              allComplete = false;
            }
          });
          
          // Clear interval when all counters reach their targets
          if (allComplete) {
            clearInterval(intervalId);
          }
          
          return newCounts;
        });
      }, 10);
      
      return () => clearInterval(intervalId);
    }
  }, [homeData]);

  useEffect(() => {
    if (popupVisible === null || !authChecked) {
      return;
    }

    const subscribed = userData?.paid_until && new Date(userData.paid_until) > new Date();

    if (popupVisible === true) {
      showCustomPopupOnceDaily();
    } else if (!subscribed) {
      showModalOnceDailyForNonMember();
    }
  }, [popupVisible, authChecked, userData]);

  const loadHomeItems = () => {
    serverInstance.post("/app/item", { class: "setting", id: "HOME_IMAGE" }, {
      headers: {
        "Content-Type": "application/json",
      }
    })
    .then(res => {
      const item = res.data.item || {};
      const serverData = {
        link: item.link || "",
        fileUrl: item.fileUrl || "",
      };
      const cached = readHomeImageCache();

      if (!isHomeImageDataEqual(cached, serverData)) {
        writeHomeImageCache(serverData);
        const nextImageUrl = buildHomeImageUrl(serverData.fileUrl);
        preloadHomeImage(nextImageUrl);
        setHomeLink(serverData.link);
        setHomeImageUrl(nextImageUrl);
      }
    })
    .catch(err => {
      console.error(err)
    })

    serverInstance.post("/app/item", { class: "popup", id: CUSTOM_POPUP_ID }, {
      headers: {
        "Content-Type": "application/json",
      }
    })
    .then(res => {
      setPopupVisible(Boolean(res.data.item?.active))
    })
    .catch(err => {
      console.error(err)
      setPopupVisible(false)
    })
  };

  const loadGuestItems = () => {
    setGuestContentLoaded(false);

    const headers = { "Content-Type": "application/json" };

    Promise.all([
      serverInstance.post("/app/item", { class: "setting", id: "GUEST_TITLE" }, { headers }),
      serverInstance.post("/app/item", { class: "setting", id: "GUEST_TEXT" }, { headers }),
      serverInstance.post("/app/item", { class: "setting", id: "GUEST_BUTTON" }, { headers }),
    ])
    .then(([titleRes, textRes, buttonRes]) => {
      const titleItem = titleRes.data.item;
      const textItem = textRes.data.item;
      const buttonItem = buttonRes.data.item;

      setHomeTitle(getLocalizedSettingText(titleItem, language, DEFAULT_GUEST_TITLE));
      setHomeText(getLocalizedSettingText(textItem, language, DEFAULT_GUEST_TEXT));
      setHomeButtonText(getLocalizedSettingText(buttonItem, language, DEFAULT_GUEST_BUTTON_TEXT));
      setHomeButtonLink(buttonItem?.link || DEFAULT_GUEST_BUTTON_LINK);
      setGuestContentLoaded(true);
    })
    .catch(err => {
      console.error(err);
      setGuestContentLoaded(false);
    });
  };

  const getUserData = () => {
    const token = localStorage.getItem("msidclienttoken")
    if (token) {
      setLoading(true)
      serverInstance.post("/client", {}, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      })
      .then(res => {
        setLoading(false)
        setUserData(res.data.data)
        if (!res.data.data.paid_until || new Date(res.data.data.paid_until) < new Date()) {
          showMemberModalOnceAfterLogin()
        }
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
      })
      .finally(() => {
        setAuthChecked(true)
      })
    }
    else {
      setAuthChecked(true)
    }
  }

  const goTo = () => {
    if (homeLink) {
      if (homeLink.includes("http") || !homeLink.startsWith("/")) {
        window.open(homeLink, "_blank");
      }
      else {
        navigate(homeLink);
      }
    }
  }

  const showCustomPopupOnceDaily = () => {
    const lastShown = localStorage.getItem('msidCustomPopupLastShown');
    const today = new Date().toDateString();

    if (!lastShown || lastShown !== today) {
      setIsCustomPopupOpen(true);
      localStorage.setItem('msidCustomPopupLastShown', today);
    }
  }

  const showModalOnceDailyForNonMember = () => {
    const lastShown = localStorage.getItem('msidModalLastShown');
    const today = new Date().toDateString();
    
    if (!lastShown || lastShown !== today) {
      setIsModalOpen(true);
      localStorage.setItem('msidModalLastShown', today);
    }
  }

  const showMemberModalOnceAfterLogin = () => {
    const lastShown = localStorage.getItem('msidMemberModalShown');
    if (!lastShown) {
      setMemberModalOpen(true);
      localStorage.setItem('msidMemberModalShown', "true");
    }
  }

  const roundToNearest5 = (num) => {
    return Math.round(num / 5) * 5;
  };

  const getItemsData = () => {
    serverInstance.get("/app/home-data", {
      headers: {
        "Content-Type": "application/json",
      }
    })
    .then(res => {
      setHomeData({
        clinic: roundToNearest5(res.data.clinic || 0),
        doctor: roundToNearest5(res.data.doctor || 0),
        journal: roundToNearest5(res.data.journal || 0),
        training: roundToNearest5(res.data.training || 0),
        meeting: roundToNearest5(res.data.meeting || 0),
      })
    })
  }

  const annualMeetingsButtonText = data?.register || (language ? "Register" : "Бүртгүүлэх");
  const isSubscribed = userData?.paid_until && new Date(userData.paid_until) > new Date();

  return (
    <div className="home-body" id="home">
      {loading ? <Dimmer/> : null}
      
      <div className="home-inner">
        {isLoggedIn ?
        <>
        {width <= 768 ? (
          <>
            {rightLinks.map((item, index) => (
              <Link key={index} className="home-real-button" to={item.link}>
                {item.text}
              </Link>
            ))}
          </>
        ) : (
          <></>
        )}
        <div className="home-img-cntnr">
          {width > 768 ? (
            <>
              {width > 768 ? (
                <>
                  {rightLinks.map((item, index) => (
                    <div key={index} className={`home-button right-home-button-${index + 1}`} data-aos={index < 3 ? 'fade-left' : ''}>
                      <div className="home-circle">
                        <Link className="home-text" to={item.link}>
                          <div className="link-text link-text-right">
                            {item.text}
                          </div>
                        </Link>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <></>
              )}            </>
          ) : (
            <></>
          )}

          <img
            key={homeImageUrl}
            src={homeImageUrl}
            className="mainImg"
            alt={data?.title}
            fetchPriority="high"
            decoding="async"
            onClick={() => goTo()}
          />

          {width > 768 ? (
            <>
              {leftLinks.map((item, index) => (
                <div key={index} className={`home-button left-home-button-${index + 1}`} data-aos={index < 3 ? 'fade-right' : ''}>
                  <div className="home-circle home-circle-left">
                    <Link className="home-text home-text-left" to={item.link}>
                      <div className="link-text link-text-left">
                        {item.text}
                      </div>
                    </Link>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <></>
          )}        </div>
        
        {width <= 768 ? (
          <>
            {leftLinks.map((item, index) => (
              <Link key={index} className="home-real-button" to={item.link}>
                {item.text}
              </Link>
            ))}          </>
        ) : (
          <></>
        )}
        </>
        :
        guestContentLoaded ?
        <div className="banner">
          <div className="title">{homeTitle}</div>
          <div className="subtitle" style={{ whiteSpace: "pre-line" }}>{homeText}</div>
          <div className="join">
            <Link to={homeButtonLink} className="btn">{homeButtonText}</Link>
          </div>
        </div>
        : null
        }
      </div>

      <CenturionSection
        centurion={data?.centurion}
        language={language}
      >
        <Link className="centurion-register-link" to="/100-anniversary">
          {annualMeetingsButtonText}
        </Link>
      </CenturionSection>

      <div className="home-data">
        <div className="home-data-container">
          {data?.dataItems.map((item, index) => (
            <Link to={item.link} key={index} className="data-item">
              <div className="number">+{animatedCounts[item.key]}</div>
              <div className="text">{item.title}</div>
            </Link>
          ))}
        </div>
      </div>

      { popupVisible === true ?
      <Modal isOpen={isCustomPopupOpen} onClose={() => setIsCustomPopupOpen(false)}>
        <CustomPopup language={language}/>
      </Modal>
      : popupVisible === false && !isSubscribed ?
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Benefits data={benefitData} isMember={false}/>
      </Modal>
      : null
      }

      <Modal isOpen={isMemberModalOpen} onClose={() => setMemberModalOpen(false)}>
        <Benefits data={benefitData} isMember={true}/>
      </Modal>
    </div>
  );
};

export default Home;
